<template>
  <DefaultLayout>
    <div class="page-header">
      <div>
        <h1>Gestión de Tenants</h1>
        <p class="subtitle">Administra tenants, usuarios y agentes de la plataforma</p>
      </div>
      <Button label="Nuevo Tenant" icon="pi pi-plus" @click="openCreateTenant" />
    </div>

    <!-- Tabla de tenants -->
    <Card>
      <template #content>
        <DataTable
          :value="tenants"
          :loading="loading"
          responsiveLayout="scroll"
          :rows="10"
          paginator
          stripedRows
          v-model:expandedRows="expandedRows"
          dataKey="id"
        >
          <!-- Expandir -->
          <Column expander style="width: 3rem" />

          <!-- Nombre -->
          <Column field="name" header="Tenant" sortable style="min-width:180px">
            <template #body="{ data }">
              <div class="tenant-cell">
                <div class="tenant-avatar">{{ data.name[0].toUpperCase() }}</div>
                <div>
                  <div class="tenant-name">{{ data.name }}</div>
                  <div class="tenant-email">{{ data.email }}</div>
                </div>
              </div>
            </template>
          </Column>

          <!-- Plan -->
          <Column field="plan_id" header="Plan" style="min-width:100px">
            <template #body="{ data }">
              <Tag v-if="data.plan_id" :value="planName(data.plan_id)" severity="info" />
              <span v-else class="text-muted">Sin plan</span>
            </template>
          </Column>

          <!-- Estado -->
          <Column field="subscription_status" header="Estado" sortable style="min-width:110px">
            <template #body="{ data }">
              <Tag :value="data.subscription_status" :severity="statusSeverity(data.subscription_status)" />
            </template>
          </Column>

          <!-- Acciones -->
          <Column header="Acciones" style="min-width:180px; text-align:right">
            <template #body="{ data }">
              <div class="action-btns">
                <Button icon="pi pi-users" text rounded size="small" v-tooltip="'Usuarios'" @click="openUsers(data)" />
                <Button icon="pi pi-android" text rounded size="small" v-tooltip="'Agentes'" @click="openAgents(data)" />
                <Button icon="pi pi-credit-card" text rounded size="small" v-tooltip="'Pagos'" @click="openPayments(data)" />
                <Button icon="pi pi-pencil" text rounded size="small" v-tooltip="'Editar'" @click="openEdit(data)" />
                <Button icon="pi pi-trash" text rounded size="small" severity="danger" v-tooltip="'Eliminar'" @click="confirmDelete(data)" />
              </div>
            </template>
          </Column>

          <!-- Fila expandida: subtenants -->
          <template #expansion="{ data }">
            <div class="subtenant-panel">
              <div class="expansion-sections">
                <div class="expansion-section">
                  <div class="subtenant-header">
                    <span><i class="pi pi-users" /> Usuarios del tenant</span>
                  </div>
                  <TenantUsersPanel :tenant-id="data.id" />
                </div>
                <div class="expansion-section">
                  <div class="subtenant-header">
                    <span><i class="pi pi-sitemap" /> Sub-tenants de {{ data.name }}</span>
                    <Button label="Nuevo sub-tenant" icon="pi pi-plus" text size="small" @click="openCreateSubtenant(data)" />
                  </div>
                  <SubTenantList :tenant-id="data.id" :tenant-agents="tenantAgentsMap[data.id] || []" ref="subListRefs" />
                </div>
              </div>
            </div>
          </template>

          <template #empty>
            <div class="empty-table">
              <i class="pi pi-users" style="font-size:2rem;color:#d1d5db" />
              <p>No hay tenants registrados.</p>
            </div>
          </template>
        </DataTable>
      </template>
    </Card>

    <!-- Dialog: Crear sub-tenant -->
    <Dialog v-model:visible="showCreateSubtenant" :header="`Nuevo sub-tenant — ${selectedTenant?.name}`" modal :style="{ width: '440px' }">
      <div class="sub-form">
        <div class="field">
          <label>Nombre <span class="req">*</span></label>
          <InputText v-model="subForm.name" class="w-full" placeholder="Ej: Sucursal Cartagena" />
        </div>
        <div class="field">
          <label>Descripción</label>
          <Textarea v-model="subForm.description" class="w-full" rows="2" autoResize placeholder="Descripción opcional..." />
        </div>
        <div class="field">
          <label>Tipo de entidad por defecto</label>
          <Dropdown
            v-model="subForm.default_company_type_code"
            :options="companyTypes"
            optionLabel="name"
            optionValue="code"
            placeholder="Sin configuración"
            showClear
            class="w-full"
          />
        </div>
        <div class="field">
          <label>Marco normativo por defecto</label>
          <Dropdown
            v-model="subForm.default_accounting_framework_code"
            :options="accountingFrameworks"
            optionLabel="name"
            optionValue="code"
            placeholder="Sin configuración"
            showClear
            class="w-full"
          />
        </div>
        <Message v-if="subError" severity="error" :closable="false">{{ subError }}</Message>
      </div>
      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showCreateSubtenant = false" />
        <Button label="Crear sub-tenant" icon="pi pi-check" :loading="savingSub" @click="createSubtenant" />
      </template>
    </Dialog>

    <!-- Dialog: Crear tenant completo -->
    <Dialog v-model:visible="showCreateTenant" header="Nuevo Tenant" modal :style="{ width: '620px' }" :maximizable="true">
      <div class="form-grid">
        <div class="form-section">
          <h4>Datos del Tenant</h4>
          <div class="field">
            <label>Nombre <span class="req">*</span></label>
            <InputText v-model="tenantForm.tenant_name" class="w-full" placeholder="Empresa S.A.S" />
          </div>
          <div class="field">
            <label>Email del tenant <span class="req">*</span></label>
            <InputText v-model="tenantForm.tenant_email" type="email" class="w-full" placeholder="contacto@empresa.com" />
          </div>
          <div class="field">
            <label>Plan</label>
            <Dropdown v-model="tenantForm.plan_id" :options="plans" optionLabel="name" optionValue="id" placeholder="Sin plan" showClear class="w-full" />
          </div>
          <div class="field">
            <label>Tipo de entidad por defecto</label>
            <Dropdown
              v-model="tenantForm.default_company_type_code"
              :options="companyTypes"
              optionLabel="name"
              optionValue="code"
              placeholder="Sin configuración"
              showClear
              class="w-full"
            />
          </div>
          <div class="field">
            <label>Marco normativo por defecto</label>
            <Dropdown
              v-model="tenantForm.default_accounting_framework_code"
              :options="accountingFrameworks"
              optionLabel="name"
              optionValue="code"
              placeholder="Sin configuración"
              showClear
              class="w-full"
            />
          </div>
        </div>

        <div class="form-section">
          <h4>Usuario Principal</h4>
          <div class="field">
            <label>Nombre completo <span class="req">*</span></label>
            <InputText v-model="tenantForm.admin_name" class="w-full" placeholder="Juan Pérez" />
          </div>
          <div class="field">
            <label>Email <span class="req">*</span></label>
            <InputText v-model="tenantForm.admin_email" type="email" class="w-full" placeholder="admin@empresa.com" />
          </div>
          <div class="field">
            <label>Contraseña <span class="req">*</span></label>
            <PasswordField v-model="tenantForm.admin_password" :auto-generate="true" />
          </div>
        </div>

        <div class="form-section full-width">
          <h4>Agentes habilitados</h4>
          <p class="field-hint">Selecciona los agentes que este tenant podrá usar y distribuir a sus subtenants.</p>
          <div class="agents-checkboxes">
            <div v-for="agent in allAgents" :key="agent.id" class="agent-check">
              <Checkbox v-model="tenantForm.agent_ids" :value="agent.id" :inputId="`agent-${agent.id}`" />
              <label :for="`agent-${agent.id}`">
                <i :class="['pi', agentIcon(agent.category)]" />
                {{ agent.name }}
                <span class="agent-cat">{{ agent.category }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <Message v-if="createError" severity="error" :closable="false">{{ createError }}</Message>

      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showCreateTenant = false" />
        <Button label="Crear Tenant" icon="pi pi-check" :loading="saving" @click="createTenant" />
      </template>
    </Dialog>

    <!-- Dialog: Usuarios del tenant -->
    <Dialog v-model:visible="showUsers" :header="`Usuarios — ${selectedTenant?.name}`" modal :style="{ width: '700px' }">
      <TenantUsersPanel v-if="selectedTenant" :tenant-id="selectedTenant.id" />
    </Dialog>

    <!-- Dialog: Agentes del tenant -->
    <Dialog v-model:visible="showAgents" :header="`Agentes habilitados — ${selectedTenant?.name}`" modal :style="{ width: '600px' }">
      <TenantAgentsPanel v-if="selectedTenant" :tenant-id="selectedTenant.id" :all-agents="allAgents" @updated="loadTenantAgents" />
    </Dialog>

    <!-- Payment Management Dialog -->
    <PaymentManagementDialog v-if="selectedPaymentTenant" :visible="showPayments" :tenant="selectedPaymentTenant" @update:visible="showPayments = $event" />

    <!-- Dialog: Editar tenant -->
    <Dialog v-model:visible="showEdit" :header="`Editar — ${editingTenant?.name}`" modal :style="{ width: '480px' }">
      <TenantForm v-if="editingTenant" :tenant="editingTenant" @saved="onTenantSaved" @cancel="showEdit = false" />
    </Dialog>

    <!-- Confirm delete -->
    <Dialog v-model:visible="showDeleteConfirm" header="Eliminar Tenant" modal :style="{ width: '400px' }">
      <p>¿Eliminar el tenant <strong>{{ deletingTenant?.name }}</strong>? Esta acción no se puede deshacer.</p>
      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showDeleteConfirm = false" />
        <Button label="Eliminar" severity="danger" :loading="deleting" @click="executeDelete" />
      </template>
    </Dialog>

    <Toast />
  </DefaultLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import DefaultLayout from '../components/layout/DefaultLayout.vue';
import SubTenantList from '../components/tenants/SubTenantList.vue';
import TenantForm from '../components/tenants/TenantForm.vue';
import TenantUsersPanel from '../components/tenants/TenantUsersPanel.vue';
import TenantAgentsPanel from '../components/tenants/TenantAgentsPanel.vue';
import PaymentManagementDialog from '../components/payments/PaymentManagementDialog.vue';
import Card from 'primevue/card';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import Message from 'primevue/message';
import Toast from 'primevue/toast';
import PasswordField from '../components/common/PasswordField.vue';
import { tenantsService } from '../services/tenants';
import api from '../services/api';

const toast = useToast();
const tenants = ref([]);
const plans = ref([]);
const allAgents = ref([]);
const companyTypes = ref([]);
const accountingFrameworks = ref([]);
const tenantAgentsMap = ref({});
const loading = ref(false);
const expandedRows = ref({});
const saving = ref(false);
const deleting = ref(false);
const createError = ref('');

const showCreateTenant = ref(false);
const showUsers = ref(false);
const showAgents = ref(false);
const showPayments = ref(false);
const showEdit = ref(false);
const showDeleteConfirm = ref(false);
const showCreateSubtenant = ref(false);
const selectedTenant = ref(null);
const selectedPaymentTenant = ref(null);
const editingTenant = ref(null);
const deletingTenant = ref(null);

const subForm = ref({
  name: '',
  description: '',
  default_company_type_code: null,
  default_accounting_framework_code: null,
});
const subError = ref('');
const savingSub = ref(false);

const tenantForm = ref({
  tenant_name: '', tenant_email: '', plan_id: null,
  default_company_type_code: null,
  default_accounting_framework_code: null,
  admin_name: '', admin_email: '', admin_password: '',
  agent_ids: [],
});

const statusSeverity = (s) => ({ active: 'success', past_due: 'warning', suspended: 'danger', inactive: 'secondary', cancelled: 'secondary' }[s] || 'info');
const planName = (id) => plans.value.find(p => p.id === id)?.name || `Plan ${id}`;
const agentIcon = (cat) => ({ financiero: 'pi-chart-line', soporte: 'pi-headphones', ventas: 'pi-shopping-cart', general: 'pi-android' }[cat] || 'pi-android');

const loadTenants = async () => {
  loading.value = true;
  try { tenants.value = await tenantsService.getTenants(); }
  catch (e) { console.error(e); }
  finally { loading.value = false; }
};

const loadTenantAgents = async () => {
  for (const t of tenants.value) {
    try {
      const res = await api.get(`/tenants/${t.id}/agents`);
      tenantAgentsMap.value[t.id] = res.data.map(a => a.agent_id);
    } catch (e) { /* ignorar */ }
  }
};

const openCreateTenant = () => {
  tenantForm.value = {
    tenant_name: '',
    tenant_email: '',
    plan_id: null,
    default_company_type_code: null,
    default_accounting_framework_code: null,
    admin_name: '',
    admin_email: '',
    admin_password: '',
    agent_ids: [],
  };
  createError.value = '';
  showCreateTenant.value = true;
};

const createTenant = async () => {
  if (!tenantForm.value.tenant_name || !tenantForm.value.admin_email || !tenantForm.value.admin_password) {
    createError.value = 'Completa los campos obligatorios';
    return;
  }
  saving.value = true;
  createError.value = '';
  try {
    await api.post('/admin/tenants/full', tenantForm.value);
    showCreateTenant.value = false;
    await loadTenants();
    await loadTenantAgents();
    toast.add({ severity: 'success', summary: 'Tenant creado', detail: `${tenantForm.value.tenant_name} creado correctamente`, life: 3000 });
  } catch (e) {
    createError.value = e.response?.data?.detail || e.message;
  } finally {
    saving.value = false;
  }
};

const openUsers = (tenant) => { selectedTenant.value = tenant; showUsers.value = true; };
const openAgents = (tenant) => { selectedTenant.value = tenant; showAgents.value = true; };
const openPayments = (tenant) => { selectedPaymentTenant.value = tenant; showPayments.value = true; };
const openEdit = (tenant) => { editingTenant.value = { ...tenant }; showEdit.value = true; };
const openCreateSubtenant = (tenant) => {
  selectedTenant.value = tenant;
  subForm.value = {
    name: '',
    description: '',
    default_company_type_code: null,
    default_accounting_framework_code: null,
  };
  subError.value = '';
  showCreateSubtenant.value = true;
};

const createSubtenant = async () => {
  if (!subForm.value.name.trim()) { subError.value = 'El nombre es obligatorio'; return; }
  savingSub.value = true; subError.value = '';
  try {
    await tenantsService.createSubTenant(selectedTenant.value.id, {
      ...subForm.value,
      tenant_id: selectedTenant.value.id,
    });
    showCreateSubtenant.value = false;
    // Expandir la fila del tenant para ver el nuevo subtenant
    expandedRows.value = { ...expandedRows.value, [selectedTenant.value.id]: true };
    toast.add({ severity: 'success', summary: 'Sub-tenant creado', detail: `${subForm.value.name} creado correctamente`, life: 3000 });
  } catch (e) {
    subError.value = e.response?.data?.detail || e.message;
  } finally {
    savingSub.value = false;
  }
};

const onTenantSaved = async () => {
  showEdit.value = false;
  await loadTenants();
  toast.add({ severity: 'success', summary: 'Actualizado', detail: 'Tenant actualizado', life: 2500 });
};

const confirmDelete = (tenant) => { deletingTenant.value = tenant; showDeleteConfirm.value = true; };
const executeDelete = async () => {
  deleting.value = true;
  try {
    await tenantsService.deleteTenant(deletingTenant.value.id);
    showDeleteConfirm.value = false;
    await loadTenants();
    toast.add({ severity: 'success', summary: 'Eliminado', detail: `Tenant ${deletingTenant.value.name} eliminado`, life: 3000 });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.response?.data?.detail || e.message, life: 3000 });
  } finally {
    deleting.value = false;
  }
};

onMounted(async () => {
  await loadTenants();
  try {
    const [plansRes, agentsRes, companyTypesRes, frameworksRes] = await Promise.all([
      api.get('/payments/plans'),
      api.get('/agents/'),
      api.get('/catalogs/company-types'),
      api.get('/catalogs/accounting-frameworks'),
    ]);
    plans.value = plansRes.data;
    allAgents.value = agentsRes.data;
    companyTypes.value = companyTypesRes.data;
    accountingFrameworks.value = frameworksRes.data;
    await loadTenantAgents();
  } catch (e) { console.error(e); }
});
</script>

<style scoped>
.page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 1.5rem; flex-wrap: wrap; gap: 1rem; }
.page-header h1 { margin: 0; color: #1e293b; }
.subtitle { color: #6b7280; margin: 0.25rem 0 0; font-size: 0.875rem; }

.tenant-cell { display: flex; align-items: center; gap: 0.75rem; }
.tenant-avatar { width: 36px; height: 36px; border-radius: 8px; background: #6366f1; color: white; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 1rem; flex-shrink: 0; }
.tenant-name { font-weight: 600; color: #1e293b; font-size: 0.9rem; }
.tenant-email { font-size: 0.78rem; color: #6b7280; }
.text-muted { color: #9ca3af; font-size: 0.85rem; }
.action-btns { display: flex; justify-content: flex-end; gap: 0.1rem; }

.subtenant-panel { padding: 1rem 1.5rem; background: #f8fafc; }
.expansion-sections { display: flex; flex-direction: column; gap: 1rem; }
.expansion-section { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 0.75rem; }
.subtenant-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; font-weight: 600; color: #374151; }

.empty-table { text-align: center; padding: 2rem; color: #9ca3af; }

/* Form */
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; }
.form-section { display: flex; flex-direction: column; gap: 0.75rem; }
.form-section.full-width { grid-column: 1 / -1; }
.form-section h4 { margin: 0 0 0.25rem; color: #1e293b; font-size: 0.9rem; border-bottom: 1px solid #f1f5f9; padding-bottom: 0.4rem; }
.field { display: flex; flex-direction: column; gap: 0.3rem; }
.field label { font-weight: 600; font-size: 0.82rem; color: #374151; }
.req { color: #ef4444; }
.field-hint { font-size: 0.78rem; color: #6b7280; margin: 0; }
.w-full { width: 100%; }

.agents-checkboxes { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 0.5rem; }
.agent-check { display: flex; align-items: center; gap: 0.5rem; padding: 0.4rem 0.6rem; border: 1px solid #e5e7eb; border-radius: 6px; }
.agent-check label { display: flex; align-items: center; gap: 0.4rem; cursor: pointer; font-size: 0.85rem; color: #374151; }
.agent-cat { font-size: 0.72rem; color: #9ca3af; }

.sub-form { display: flex; flex-direction: column; gap: 0.75rem; }
</style>
